import CheckIcon from '@mui/icons-material/Check';
import { Box, Stack, Typography } from '@mui/material';

import type { RecipientStatus } from '@/types/roc/models/RecipientStatus';

import {
  buildJourneySteps,
  type JourneyStep,
  type StatusTone
} from '../lib/referralDisplay';

interface ReferralJourneyStepperProps {
  status: RecipientStatus;
}

const TONE_COLOR: Record<StatusTone, string> = {
  neutral: 'text.secondary',
  info: 'primary.main',
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main'
};

function StepDot({
  state,
  tone
}: {
  state: JourneyStep['state'];
  tone?: StatusTone;
}) {
  if (state === 'complete') {
    return (
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          bgcolor: 'success.main',
          color: 'success.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        <CheckIcon sx={{ fontSize: 14 }} />
      </Box>
    );
  }
  const isCurrent = state === 'current';
  const currentColor = tone ? TONE_COLOR[tone] : 'primary.main';
  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        flexShrink: 0,
        border: '2px solid',
        borderColor: isCurrent ? currentColor : 'divider',
        bgcolor: isCurrent ? currentColor : 'transparent'
      }}
    />
  );
}

export function ReferralJourneyStepper({
  status
}: ReferralJourneyStepperProps) {
  const steps = buildJourneySteps(status);

  return (
    <Stack gap={0}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.6,
          color: 'text.secondary',
          textTransform: 'uppercase',
          mb: 1.5
        }}
      >
        Referral Journey
      </Typography>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const isDone = step.state === 'complete';
        return (
          <Stack key={step.key} direction="row" gap={1.5}>
            <Stack alignItems="center">
              <StepDot state={step.state} tone={step.tone} />
              {!isLast && (
                <Box
                  sx={{
                    width: 2,
                    flex: 1,
                    minHeight: 40,
                    bgcolor: isDone ? 'success.main' : 'grey.200',
                    my: 0.25
                  }}
                />
              )}
            </Stack>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: step.state === 'current' ? 700 : 500,
                color:
                  step.state === 'upcoming' ? 'text.secondary' : 'text.primary',
                pb: isLast ? 0 : 2.5
              }}
            >
              {step.label}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}
